/**
 * One-tap "Add" on Comma & Co product cards (rails, grids, bento tiles).
 *
 * Each card carries a plain <form class="cc-add" action="/cart/add"> holding the
 * first available variant, so it still works as a normal post with scripts off.
 * With scripts on, the add goes through the same steps Horizon's product form
 * uses: announce a CartLinesUpdateEvent, post to cart/add.js with the cart
 * drawer's section ids, then resolve the event with the fresh cart. The drawer,
 * the header count and the fly-to-cart all listen for that event, so they react
 * exactly as they do for an add from a product page.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const refreshCart = async () => {
  const items = document.querySelector('cart-items-component');
  if (items) {
    await customElements.whenDefined('cart-items-component');
    return items.fetchCartData();
  }
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
};

const setState = (button, state) => {
  if (!button) return;
  button.dataset.state = state;
  button.setAttribute('aria-busy', state === 'busy' ? 'true' : 'false');
};

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('cc-add')) return;
  event.preventDefault();

  const button = form.querySelector('button');
  if (button?.dataset.state === 'busy') return;
  setState(button, 'busy');

  const formData = new FormData(form);
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity: Number(formData.get('quantity')) || 1 }],
      promise: deferred.promise,
    })
  );

  const config = fetchConfig('javascript', { body: formData });
  fetch(Theme.routes.cart_add_url, { ...config, headers: { ...config.headers, Accept: 'text/html' } })
    .then((response) => response.json())
    .then(async (response) => {
      const ajaxCart = await refreshCart();
      if (response.status) {
        form.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items, source: 'cc-quick-add', itemCount: 1 },
        });
        setState(button, 'error');
        if (button) button.title = response.description || response.message || '';
        setTimeout(() => setState(button, 'idle'), 2600);
        return;
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'cc-quick-add',
          itemCount: 1,
          sections: response.sections,
          didError: false,
        },
      });
      setState(button, 'added');
      setTimeout(() => setState(button, 'idle'), 1800);
    })
    .catch((error) => {
      console.error(error);
      deferred.reject(error);
      form.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      setState(button, 'idle');
    });
});
